"use client";

import type { ButtonProps } from "@ui/components/core/Button";

import { Button } from "@ui/components/core/Button";
import { useInView } from "@ui/lib/useInView";
import { Volume2Icon, VolumeXIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@ui/lib/utils";

interface SimpleVideoPlayerControlledProps {
  autoPlay?: boolean;
  /**
   * Additional classes for the container. Use this to set aspect ratio (e.g., "aspect-1x1", "aspect-16x9", "aspect-[13/12]")
   */
  className?: string;
  isMuted: boolean;
  onMuteToggle: () => void;
  onVideoClick?: () => void;
  onVideoRef?: (ref: HTMLVideoElement | null) => void;
  preload?: "auto" | "metadata" | "none";
  showMuteButton?: boolean;
  src: string;
}

/**
 * Controlled version of SimpleVideoPlayer - all state managed by parent
 */
export const SimpleVideoPlayerControlled = ({
  src,
  isMuted,
  onMuteToggle,
  onVideoRef,
  onVideoClick,
  autoPlay = true,
  preload = "auto",
  showMuteButton = true,
  className = "",
}: SimpleVideoPlayerControlledProps) => {
  return (
    <div className={cn("relative mb-8 w-full overflow-hidden rounded-xl", className)}>
      <video
        autoPlay={autoPlay}
        className='h-full w-full object-cover'
        loop
        muted={isMuted}
        playsInline
        preload={preload}
        ref={onVideoRef}
        src={src}
        onClick={onVideoClick}
        style={{ cursor: onVideoClick ? "pointer" : undefined }}
      />
      {showMuteButton && (
        <Button
          className='absolute right-12 top-12'
          onClick={e => {
            e.stopPropagation();
            onMuteToggle();
          }}
          size='pill'
          variant='elevated'
        >
          {!isMuted ? <Volume2Icon fill='currentColor' size={20} /> : <VolumeXIcon fill='currentColor' size={20} />}
        </Button>
      )}
    </div>
  );
};

interface SimpleVideoPlayerProps {
  autoPlayWhenInView?: boolean;
  fullWidth?: boolean;
  /**
   * Additional classes for the container. Use this to set aspect ratio (e.g., "aspect-1x1", "aspect-16x9", "aspect-[13/12]")
   */
  className?: string;
  initialMuted?: boolean;
  breakoutMuteButton?: boolean;
  muteButtonVariant?: ButtonProps["variant"];
  onVideoRef?: (ref: HTMLVideoElement | null) => void;
  showMuteButton?: boolean;
  src: string;
  fit?: "cover" | "contain";
}

/**
 * Uncontrolled version of SimpleVideoPlayer - manages its own state
 */
export const SimpleVideoPlayer = ({
  src,
  autoPlayWhenInView = false,
  initialMuted = true,
  breakoutMuteButton = false,
  muteButtonVariant = "elevated",
  onVideoRef,
  showMuteButton = true,
  fullWidth = true,
  className = "",
  fit = "cover",
}: SimpleVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(initialMuted);

  // Use the existing useInView hook for mobile autoplay
  const isInView = useInView(videoRef, { threshold: 0.5, once: false });

  // Handle autoplay when video comes into view on mobile
  useEffect(() => {
    if (autoPlayWhenInView && isInView && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay failed, user interaction required
      });
    }
  }, [autoPlayWhenInView, isInView]);

  // Forward the video ref to parent
  useEffect(() => {
    if (onVideoRef && videoRef.current) {
      onVideoRef(videoRef.current);
    }
  }, [onVideoRef]);

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  return (
    <div
      className={cn(
        "mb-8 overflow-hidden rounded-xl",
        fullWidth && "w-full",
        breakoutMuteButton ? "" : "relative",
        className,
      )}
    >
      <video
        autoPlay={!autoPlayWhenInView}
        className={cn("h-full w-full", fit === "cover" ? "object-cover" : "object-contain")}
        loop
        muted={isMuted}
        playsInline
        preload={autoPlayWhenInView ? "metadata" : "auto"}
        ref={videoRef}
        src={src}
      />
      {showMuteButton && (
        <Button className='absolute right-12 top-12' onClick={handleMuteToggle} size='pill' variant={muteButtonVariant}>
          {!isMuted ? (
            <Volume2Icon fill='none' size={20} className='[&>path:first-child]:fill-current' />
          ) : (
            <VolumeXIcon fill='currentColor' size={20} />
          )}
        </Button>
      )}
    </div>
  );
};

// Legacy exports for backwards compatibility
export const SimpleSquareVideo = SimpleVideoPlayer;
export const SimpleSquareVideoControlled = SimpleVideoPlayerControlled;
